import React from "react";

/**
 * Decorative engraved divider that sits between major sections.
 * Two thin rules with a centered ornamental glyph: brass diamond + dot
 * row. Mirrors the rule-engraved class in ledger.css but presented as
 * a real spacer block so each section's edge is felt rather than
 * abrupt.
 *
 * Three stylistic variants - choose by prop:
 *   variant="diamond" (default) - three dots and a brass diamond
 *   variant="seal"   - small wax-seal style mark, more emphatic
 *   variant="rule"   - pure double rule, lowest visual weight
 */
export default function SectionDivider({ variant = "diamond", label }) {
  return (
    <div
      aria-hidden={!label}
      className="relative w-full flex items-center justify-center px-4 sm:px-8"
      style={{ paddingTop: 14, paddingBottom: 14 }}
    >
      {/* Left rule */}
      <span
        className="rule"
        style={{ flex: 1, maxWidth: "min(40%, 280px)", height: 1 }}
      />

      {/* Centre ornament */}
      <span
        className="px-6"
        style={{ display: "inline-flex", alignItems: "center", gap: 14 }}
      >
        {variant === "rule" && (
          <span style={{ width: 60, height: 4, borderTop: "1px solid var(--brass-rule)", borderBottom: "1px solid var(--brass-rule)" }} />
        )}
        {variant === "diamond" && <DiamondMark />}
        {variant === "seal" && (
          <span
            className="wax-seal"
            style={{ width: 32, height: 32, fontSize: 14, background: "radial-gradient(circle at 30% 25%, #C9A55F 0%, var(--brass) 50%, #6B5128 100%)" }}
          >
            ❦
          </span>
        )}
        {label && (
          <span className="mono" style={{ fontSize: 11, letterSpacing: "0.14em", color: "var(--brass-deep)", textTransform: "uppercase" }}>
            {label}
          </span>
        )}
      </span>

      {/* Right rule */}
      <span
        className="rule"
        style={{ flex: 1, maxWidth: "min(40%, 280px)", height: 1 }}
      />
    </div>
  );
}

function DiamondMark() {
  return (
    <svg width="56" height="12" viewBox="0 0 56 12" aria-hidden="true">
      <circle cx="6"  cy="6" r="1.4" fill="var(--brass)" />
      <circle cx="18" cy="6" r="1.4" fill="var(--brass)" />
      <g transform="translate(28 6) rotate(45)">
        <rect x="-3" y="-3" width="6" height="6" fill="none" stroke="var(--brass)" strokeWidth="0.8"/>
        <rect x="-1.2" y="-1.2" width="2.4" height="2.4" fill="var(--vermilion)"/>
      </g>
      <circle cx="38" cy="6" r="1.4" fill="var(--brass)" />
      <circle cx="50" cy="6" r="1.4" fill="var(--brass)" />
    </svg>
  );
}
